<template>
  <el-card class="song-item" v-loading="loading">
    <!-- 替换音频 -->
    <verify-uploader
      v-if="!song.uploadPercent"
      class="replace"
      action="/action/upload?type=track"
      :accept="`.${trackType.join(',.')}`"
      :before-upload="handleMediaBefore"
      :on-progress="handleMediaProgress"
      :on-success="handleMediaSuccess"
      :on-error="handleMediaError"
      :show-file-list="false"
    >
      <el-button type="primary" class="m-b-sm">上传音频</el-button>
      <span slot="tip" class="el-upload__tip">只能上传.mp3/.flac/.ogg/.wav/.ape/.wma文件</span>
    </verify-uploader>
    <!-- 上传中 -->
    <div class="process" v-if="!song.mediaFinish || !song.media" @click.stop.prevent>
      <el-progress :percentage="song.uploadPercent" :show-text="false" class="progress"></el-progress>
      <span v-if="song.uploadPercent === 100">音频检测中..</span>
    </div>
    <player v-if="howlerData.src.length" :autoplay="false" :howler-data="howlerData"></player>

    <!-- 音频异常 -->
    <div v-if="!song.uploadPercent && song.hasError" @click.stop.prevent>
      <span class="error">音频存在严重音质问题, 请重新上传</span>
      <check-icon :track="{fileCheck: song.media.checkQuality}"></check-icon>
    </div>

    <el-form
      ref="trackForms"
      :rules="rules"
      :model="song"
      :disabled="disabledEdit"
      :inline-message="true"
      :validate-on-rule-change="false"
      label-width="100px"
    >
      <el-tabs v-model="activeTab" type="card">
        <el-tab-pane label="基本信息" name="basic">
          <span slot="label">基本信息<span class="danger-word">*</span></span>
          <el-form-item label="封面" prop="coverList">
            <div class="avatar">
              <div class="pic">
                <img
                  v-if="song.coverList.length"
                  :src="song.coverList[0].url"
                  class="pic-img"
                >
              </div>
              <div class="content">
                <p class="line">图片必须为jpg、jpeg、png格式</p>
                <p class="line">正方形，尺寸 > 750*750</p>
                <verify-uploader
                  class="upload-avatar"
                  action="/action/upload?type=image"
                  :show-file-list="false"
                  :on-preview="handlePreview"
                  :on-remove="handleRemove"
                  :on-error="handleError"
                  :on-success="handleSuccess"
                  :before-upload="handleBeforeUpload"
                  :file-list="song.coverList"
                  :disabled="uploading"
                  :accept="`.${imageType.join(',.')}`"
                >
                  <el-button size="small" type="primary" :disabled="uploading">
                    {{ uploadText }}
                  </el-button>
                </verify-uploader>
              </div>
            </div>
          </el-form-item>
          <warn-form-item label="歌曲名" prop="trackName">
            <el-input
              v-model.trim="song.trackName"
              placeholder="请输入音乐作品官方用名"
              class="form-field"
              v-trans-complex="'歌曲名'"
            ></el-input>
          </warn-form-item>
          <el-form-item label="语言" prop="language">
            <el-select v-model="song.language" filterable class="form-field">
              <el-option
                v-for="(name, key) in languageMap"
                :key="key"
                :data-key="key"
                :value="+key"
                :label="name"
              ></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="生成工具" prop="trackExtend.aiGentool">
            <el-input
              v-model="song.trackExtend.aiGentool"
              placeholder="请填写您的生成工具名称"
              class="form-field"
              @blur="handleTextBlur('trackExtend.aiGentool')"
            ></el-input>
          </el-form-item>

          <el-form-item label="生成时间戳" prop="trackExtend.aiTimestamp">
            <el-date-picker
              v-model="song.trackExtend.aiTimestamp"
              type="datetime"
              class="form-field"
              placeholder="请选择生成时间戳"
              value-format="yyyy-MM-dd HH:mm:ss"
            ></el-date-picker>
          </el-form-item>

          <el-form-item label="内容实体标识" prop="trackExtend.contentEntityId">
            <el-input
              v-model="song.trackExtend.contentEntityId"
              placeholder="请填写内容生成唯一标识ID"
              maxlength="32"
              class="form-field"
              @blur="handleTextBlur('ctrackExtend.ontentEntityId')"
            ></el-input>
            <el-tooltip class="item" effect="dark" placement="right">
              <i class="el-icon-info" />
              <div slot="content" class="content-entity-tip-content">
                生成工具生成内容ID，包括英文字母、数字的组合。该ID具有唯一标识内容的功能。例如常用的AI音乐工具Suno生成作品链接：
                <br>
                https://suno.com/song/a5e2198a-f352-4abb-9a24-7f81b143ded3的实体标识为
                <br>
                <span class="highlight">a5e2198af3524abb9a247f81b143ded3</span>
              </div>
            </el-tooltip>
          </el-form-item>

          <lyric
            ref="lyric"
            :lyric="song.lyricData"
            :media="song.media"
            :track="song"
          ></lyric>
        </el-tab-pane>
      </el-tabs>
    </el-form>

    <div class="center m-t-lg">
      <p class="m-b-lg submit-tip">AI 音频上传时请仔细核对信息，上传后暂不支持修改。</p>
      <el-button type="primary" @click="handleSave">提 交</el-button>
    </div>
  </el-card>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import cloneDeep from 'lodash-es/cloneDeep'
import VerifyUploader from '@/components/VerifyUploader'
import Player from '@/components/Player'
import trackService from '@/api/track'
import { WarnError, SBCcaseList, trackType, imageType } from '@/utils/constants'
import { validateFileExt } from '@/utils/validate'
import { isAudioCheckError } from '@/utils/fileUtil'
import AiSong from '@/models/AiSong'
import Lyric from '@/views/aiAlbum/components/Lyric'
import {
  languageMap, isOnlyMap, canAuthMap, lyricList
} from '@/views/album/mixin.js'
import {
  validateError,
  fileFieldConfigList,
} from './const'
import { set, get } from 'lodash-es'

dayjs.extend(customParseFormat)

export default {
  name: 'AiSongItem',
  components: {
    Lyric,
    VerifyUploader,
    Player,
  },
  data() {
    // http://tapd.oa.com/tme_music_lib/prong/stories/view/1020369272858599991
    const nameValidator = (rule, value, callback) => {
      const ruleList = [
        {
          errorMsg: '歌曲名不得包含发行信息',
          test() {
            return /\d{4,}/g.test(value)
          }
        },
        {
          errorMsg: '歌曲名不得包含曲序/编号信息',
          test() {
            return /^\d.*/g.test(value)
          }
        },
        {
          errorMsg: '串烧歌曲请使用加号“+”或斜杆“/”分隔',
          test() {
            return validateError.seriesErrorList.some(series => value.includes(series))
          }
        },
        {
          errorMsg: '歌曲名请勿使用广义名称',
          test() {
            return ['曲目', '器乐'].some(universal => value.includes(universal))
          }
        },
        {
          errorMsg: '非官方要求请使用英文半角符号',
          test() {
            return SBCcaseList.some(sbc => value.includes(sbc))
          }
        },

      ]

      for (const rule of ruleList) {
        if (rule.test()) {
          return callback(new WarnError(rule.errorMsg))
        }
      }

      callback()
    }

    return {
      languageMap,
      isOnlyMap,
      canAuthMap,
      trackType,

      previewShow: false,
      previewUrl: '',
      imageType,
      uploading: false,

      activeTab: 'basic',
      editType: 'word',
      // trackId: 0,
      loading: false,
      rules: {
        coverList: { type: 'array', required: true, message: '请上传至少一张封面', trigger: 'change' },
        trackName: [
          { required: true, message: '请输入歌曲名', trigger: 'blur' },
          { validator: nameValidator, warnType: 'notify', trigger: 'blur' }
        ],
        language: { type: 'number', required: true, message: '请选择语言', trigger: 'change' },
        'trackExtend.aiGentool': { required: true, message: '请填写您的生成工具名称', trigger: 'blur' },
        'trackExtend.aiTimestamp': { required: true, message: '请选择生成时间戳', trigger: 'blur' },
        'trackExtend.publicTime': { required: true, message: '请选择发行时间', trigger: 'blur' },
      },
      song: new AiSong(),
      howlerData: { src: [] }
    }
  },
  computed: {
    ...mapGetters([
      'userInfo',
    ]),
    /**
     * @returns {Boolean}
     */
    hasBasicAuth() {
      // return this.$store.getters.isEditor
      return true
    },
    /**
     * @returns {Boolean}
     */
    disabledEdit() {
      return !this.hasBasicAuth
    },
    /**
     * @returns {String}
     */
    uploadText() {
      return this.song.coverList.length ? '修改图片' : '上传图片'
    },
  },
  created() {
    // let trackId
    // if ((trackId = this.$route.query.trackId)) {
    //   this.trackId = trackId
    //   this.fetchData(trackId)
    // }
  },
  methods: {
    // async fetchData() {
    //   this.loading = true
    //   try {
    //     const result = await trackService.queryTrackDetail(this.trackId)
    //     this.initTrack(result)
    //   } catch (error) {
    //     this.$alert(`查询异常！${error.message}`, '提示', { type: 'error', error })
    //   } finally {
    //     this.loading = false
    //   }
    // },
    initTrack(track) {
      const trackObj = new AiSong({
        ...track,
        payment: track.trackPayment || { /* paymentUpdate: 0 */ },
      })

      trackObj.mediaFinish = true
      trackObj.uploadPercent = 0
      trackObj.buTrace = track.buTrace || {}

      // 把已经处理过的数据保存一份, 最后对比时使用
      trackObj.oldLockObj = Object.freeze(cloneDeep({
        trackPayment: trackObj.trackPayment,
        // 核心基础信息
        trackName: trackObj.trackName,
        coverList: trackObj.coverList,
        trackExtend: {},
        publicTime: trackObj.publicTime,
        media: trackObj.media,
        lyricData: trackObj.lyricData
      }))

      fileFieldConfigList.forEach(config => {
        if (!trackObj[config.key]) {
          trackObj[config.key] = []
        }
      })

      this.song = trackObj
      this.howlerData = { src: [trackObj?.media?.mediaUrl] }
    },

    handleMediaProgress(event, file, fileList) {
      this.song.uploadPercent = event.percent
    },

    /**
     * @param {Object} file 和其他 file 对象属性一样,多了 md5 这个值
     */
    handleMediaBefore(file) {
      if (!validateFileExt(trackType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return Promise.reject()
      }

      this.song.mediaFinish = false
    },

    handleMediaError(error, file, fileList) {
      this.$alert(`<${file.name}>上传失败，请稍后重试`, '提示', {
        error,
        type: 'error',
      })
      this.song.mediaFinish = true
    },

    handleMediaSuccess(resp, file, fileList) {
      const respData = resp.data
      this.song.mediaFinish = true // 表示音频上传完成, 无论有没严重错误
      this.song.hasError = isAudioCheckError(respData.file_info.audio_check)
      this.song.uploadPercent = 0
      this.song.media = {
        mediaUrl: respData.url,
        mediaPath: respData.file_id,
        using: 1,
        remarks: this.fileRemarks,
        fileType: file.name.slice(file.name.lastIndexOf('.') + 1),
        fileSize: file.size,
        fileName: file.name,
        md5: respData.md5,
        duration: respData.file_info.duration != null ? respData.file_info.duration : 0,
        checkQuality: respData.file_info.audio_check,
      }

      this.howlerData = { src: [respData.url] }
    },

    getLyricData() {
      const ld = {}
      const lyricData = this.$refs.lyric.getLyricData()
      for (const lyricType of lyricList) {
        if (lyricData[lyricType.key].fileList[0]) {
          ld[lyricType.key] = lyricData[lyricType.key].fileList[0]
          ld[lyricType.key].fFrom = lyricData[lyricType.key].from
        } else {
          ld[lyricType.key] = null
        }
      }

      return ld
    },

    handleSave() {
      this.$refs.trackForms.validate(async(valid) => {
        if (!valid) return

        if (this.uploading) {
          return this.$message.warning('专辑图上传中')
        }
        if (!this.song.mediaFinish) {
          return this.$alert('歌曲音频文件未上传完成, 请完成后提交', '提示')
        }
        if (this.song.hasError) {
          return this.$alert('歌曲音频文件存在严重问题, 请重新上传', '提示')
        }

        const trackObj = cloneDeep(this.song)
        trackObj.lyricData = this.getLyricData()

        // if (trackObj.trackId) {
        //   // 解锁了, 检查是否有字段变化
        //   // 当前逻辑是哪怕字段修改申请 请求异常，还是可以修改专辑提交成功的
        //   try {
        //     await checkAndApplyModify(trackObj)
        //   } catch (error) {
        //     return
        //   }

        //   // 扩展字段只重置审核字段
        //   const trackExtend = Object.assign({}, trackObj.trackExtend, trackObj.oldLockObj.trackExtend)
        //   Object.assign(trackObj, trackObj.oldLockObj, { trackExtend })
        // }
        trackObj.oldLockObj = undefined

        this.save(trackObj)
      })
    },

    async save(postData) {
      this.loading = true
      try {
        await trackService.saveAiTrack({
          newTrackList: [postData]
        })
        this.$autoCloseMsg(3000, {
          type: 'success',
          message: '您的修改已成功提交，如涉及敏感字段修改还需审核！3秒后转跳回列表页。'
        })

        setTimeout(_ => {
          this.$router.push({
            name: 'aiTrackList',
          })
        }, 3000)
      } catch (error) {
        this.$alert(`提交异常（${error.message}）`, '提示', { type: 'error', error })
      } finally {
        this.loading = false
      }
    },

    handleTextBlur(key) {
      set(this.song, key, get(this.song, key).trim())
    },

    handlePreview(file) {
      this.previewUrl = file.url
      this.previewShow = true
    },

    handleRemove(file, fileList) {
      this.song.coverList = fileList
    },

    handleError(err, file, fileList) {
      this.uploading = false
      this.$message.error('上传失败: ' + err.message)
    },

    handleSuccess(resp, file, fileList) {
      this.uploading = false
      const respData = resp.data
      const fileData = {
        url: this.$toPreview(respData.url),
        md5: respData.md5,
        photoPath: respData.file_id,
        photoUrl: respData.url,
        photoSize: file.size,
        photoHeight: respData.file_info.height,
        photoWidth: respData.file_info.width,
        orderIndex: 0,
      }
      this.song.coverList = [fileData]
    },

    handleBeforeUpload(file) {
      if (!validateFileExt(imageType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return Promise.reject()
      }
      if (this.song.coverList.length === 0) {
        this.uploading = true
        return Promise.resolve()
      }

      return this.$confirm('只能上传一张封面，是否覆盖旧的封面?', '提示', {
        confirmButtonText: '覆盖',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.uploading = true
        return Promise.resolve()
      }).catch(() => {
        return Promise.reject()
      })
    },
  }
}
</script>

<style lang="scss">
.song-item {
  position: relative;

  .el-form {
    position: relative;
  }

  .form-field {
    width: 400px;
  }

  .avatar {
    display: inline-block;
    width: 400px;
    height: 120px;
    background-color: #fafafa;
    vertical-align: middle;
    border-radius: 4px;
    overflow: hidden;

    .pic {
      position: relative;
      float: left;
      width: 120px;
      height: 120px;
      background: #f0f0f0 url("~@/assets/images/album.png") 50% no-repeat;
      background-size: 80px 80px;
    }
    .pic-img {
      width: 120px;
      height: 120px;
      border: none;
    }

    .content {
      padding: 24px;
      float: left;
    }

    .line {
      line-height: 20px;
      color: rgba(0,0,0,.45);
      font-size: 12px;
    }
  }
  .upload-avatar {
    display: inline-block;
    vertical-align: middle;
  }
}

.content-entity-tip-content {
  width: 360px;

  .highlight {
    color: red;
  }
}

.submit-tip {
  font-weight: bold;
}

.el-upload__tip {
  margin-left: 5px;
}

</style>
